import * as z from 'zod/mini'
import { keyedIssue, refineWithKey } from '../errors/validation-keys'
import { WeekDay } from '../types'
import { timeOfDay } from './common'
import { reservationFields } from './reservation'

/** One bookable window on one weekday, `HH:mm` to `HH:mm` of the premise's wall-clock time. */
export const reservationInterval = z
  .object({ weekDay: z.enum(WeekDay), startTime: timeOfDay, endTime: timeOfDay })
  .check(
    z.superRefine((value, ctx) => {
      // `HH:mm` strings order the same way the times do.
      if (value.endTime <= value.startTime) {
        ctx.addIssue(keyedIssue('validation.endAfterStart', ['endTime']))
      }
    }),
  )

/**
 * Field rules for a reservation blueprint (a bookable service), shared by the HTTP bodies and the
 * domain operation. Durations are minutes, at most one day. `description` is HTML from the manager's
 * editor, shown to customers in the public booking widget; the API sanitises it, the limit counts the
 * markup too, and `''` means none (as for events, docs/events.md).
 */
export const reservationBlueprintFields = {
  name: reservationFields.name,
  description: z.string().check(z.maxLength(5000)),
  price: z.number().check(z.minimum(0), z.maximum(999999)),
  duration: z.int().check(z.positive(), z.maximum(1440)),
  durationAfter: z.int().check(z.nonnegative(), z.maximum(1440)),
  cancellationDeadline: reservationFields.cancellationDeadline,
  /** The booking widget offers "Poznámka pre prevádzku" (docs/reservations.md, _Client note_). */
  allowCustomerNote: z.boolean(),
  intervals: z
    .array(reservationInterval)
    .check(refineWithKey('validation.intervalsRequired', (intervals: unknown[]) => intervals.length > 0)),
}
